import React, { useEffect, useMemo, useState } from "react";
import { Drawer } from "@mui/material";
import { GripVertical } from "lucide-react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import amplitude from "../../utils/ampli";
import { auth } from "../../redux/slices/authSlice";
import {
  pricing,
  setColumnVisibilityRedux,
  setUpdatedColumnOrder,
} from "../../redux/slices/pricingSlice";

const COLUMN_DISPLAY_NAMES = {
  "Use Rap": "Repl Rap",
  "Use Price": "Repl PPC",
  "LD Reco Diff": "Repl Diff",
  "Rap Rank": "Rank",
  "Rap Matching count": "Count",
  "Rap Rank Diff": "Rank Change",
  "Rap Count Diff": "Count Change",
  "Inventory Count": "My Stock",
  "Sales Count": "My Sales",
  "Rank Percentile": "Rank %",
  "Total Availability": "Broad Count",
  "Key To Symbol": "Key To Symbols",
  "Band Low": "Band Range",
  "Report Date": "Last Fetched Date",
  "Repriced_At": "Repriced Date",
};

const OBSOLETE_COLUMN_IDS = new Set([
  "LD Spec",
  "Use Price",
  "Use Rap",
  "LD Reco Diff",
  "LD Rank",
  "LD Count",
]);

const ColumnOrderDrawer = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const token = useSelector(auth).token;
  const accountDetails = useSelector(auth).userDetails;
  const configColumnOrder = useSelector(pricing).config?.column_orders;

  const appliedOrder = useMemo(
    () => Array.isArray(configColumnOrder)
      ? configColumnOrder.filter((c) => c.id !== "select").map((c) => c.id)
      : [],
    [configColumnOrder]
  );

  const appliedVisibility = useMemo(() => {
    if (!Array.isArray(configColumnOrder)) return {};
    const map = {};
    configColumnOrder.forEach((c) => {
      if (c.id !== "select") map[c.id] = c.show !== false;
    });
    return map;
  }, [configColumnOrder]);

  const [draftOrder, setDraftOrder] = useState(appliedOrder);
  const [draftVisibility, setDraftVisibility] = useState(appliedVisibility);
  const [draggingColumnId, setDraggingColumnId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectNoneWarning, setSelectNoneWarning] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraftOrder(appliedOrder);
    setDraftVisibility(appliedVisibility);
    setDraggingColumnId(null);
  }, [open, appliedOrder, appliedVisibility]);

  const orderChanged = useMemo(
    () => JSON.stringify(draftOrder) !== JSON.stringify(appliedOrder),
    [draftOrder, appliedOrder]
  );

  const visibilityChanged = useMemo(() => {
    const ids = new Set([...appliedOrder, ...draftOrder]);
    for (const id of ids) {
      const draft = draftVisibility[id] !== false;
      const applied = appliedVisibility[id] !== false;
      if (draft !== applied) return true;
    }
    return false;
  }, [draftOrder, appliedOrder, draftVisibility, appliedVisibility]);

  const hasChanges = orderChanged || visibilityChanged;

  const handleDragStart = (event, columnId) => {
    setDraggingColumnId(columnId);
    event.dataTransfer.setData("text/plain", columnId);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (event, targetColumnId) => {
    event.preventDefault();
    const sourceColumnId =
      draggingColumnId || event.dataTransfer.getData("text/plain");

    if (!sourceColumnId || sourceColumnId === targetColumnId) {
      return;
    }

    setDraftOrder((prev) => {
      const next = [...prev];
      const sourceIndex = next.indexOf(sourceColumnId);
      const targetIndex = next.indexOf(targetColumnId);

      if (sourceIndex === -1 || targetIndex === -1) {
        return prev;
      }

      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);

      return next;
    });
    setDraggingColumnId(null);
  };

  const handleToggleVisibility = (columnId) => {
    setSelectNoneWarning(false);
    setDraftVisibility((prev) => {
      const isVisible = prev[columnId] !== false;
      const visibleCount = draftOrder.reduce(
        (count, id) => count + (prev[id] !== false ? 1 : 0),
        0
      );

      if (isVisible && visibleCount <= 1) {
        return prev;
      }

      return {
        ...prev,
        [columnId]: !isVisible,
      };
    });
  };

  const handleReset = () => {
    setDraftOrder(appliedOrder);
    setDraftVisibility(appliedVisibility);
  };

  const handleSave = async () => {
    if (isSaving || !hasChanges) return;
    const visibleCount = draftOrder.filter((id) => draftVisibility[id] !== false).length;
    if (visibleCount < 2) {
      setSelectNoneWarning(true);
      return;
    }
    setSelectNoneWarning(false);

    setIsSaving(true);

    try {
      // Build updated config column_orders with new {id, show} format
      const updatedConfigOrder = [
        { id: "select", show: false },
        ...draftOrder.map((id) => ({ id, show: draftVisibility[id] !== false })),
      ];
      dispatch(setUpdatedColumnOrder(updatedConfigOrder));

      if (accountDetails?.account_id && token) {
        const url = `${process.env.REACT_APP_API_URL}/update_pricing_report_market_config`;
        await axios.post(
          url,
          {
            seller_account_id: accountDetails.account_id,
            column_orders: updatedConfigOrder,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      amplitude.track({
        event_type: "v2_pricetable_reorder",
        event_properties: {
          source: "column_drawer",
          columnOrder: updatedConfigOrder,
        },
      });

      // Sync visibility to Redux for priceTable to consume immediately
      const nextVisibility = {};
      draftOrder.forEach((id) => {
        nextVisibility[id] = draftVisibility[id] !== false;
      });
      nextVisibility.select = true;

      dispatch(setColumnVisibilityRedux(nextVisibility));
      onClose?.({ type: "click" });
    } catch (error) {
      console.error("Failed to save column order", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer anchor={"right"} open={open} onClose={onClose}>
      <div className="w-[90vw] min-w-[320px] md:w-[30vw] md:min-w-[450px] md:max-w-[450px] min-h-full h-full flex flex-col overflow-hidden">
        <header className="min-h-[60px] border-x-0 border-t-0 border-b border-solid border-[#D8D8D8] flex items-center gap-4">
          <svg
            className="ml-4 cursor-pointer"
            xmlns="http://www.w3.org/2000/svg"
            width="15px"
            height="15px"
            viewBox="0 -2 32 32"
            onClick={onClose}
          >
            <g id="Lager_86" data-name="Lager 86" transform="translate(0 -3)">
              <path
                id="Path_94"
                data-name="Path 94"
                d="M16.587,6.42,27.167,17,16.587,27.58a2,2,0,0,0,2.833,2.833L31.414,18.42a2.013,2.013,0,0,0,0-2.84L19.42,3.587A2,2,0,0,0,16.587,6.42Z"
                fill="#161615"
              />
              <path
                id="Rectangle_45"
                data-name="Rectangle 45"
                d="M0,0H26a2,2,0,0,1,2,2V2a2,2,0,0,1-2,2H0A0,0,0,0,1,0,4V0A0,0,0,0,1,0,0Z"
                transform="translate(28 19) rotate(180)"
                fill="#161615"
              />
            </g>
          </svg>
          <h4 className="m-0 font-medium text-base">Columns</h4>
        </header>

        <div className="px-4 py-2 border-b border-solid border-[#EAEAEA] flex-col items-center justify-between">

          <div className="flex items-center gap-2">
            <button
              className="text-xs text-[#1E90FF] cursor-pointer bg-transparent border-none p-0"
              onClick={() =>
                setDraftVisibility(
                  Object.fromEntries(draftOrder.map((id) => [id, true]))
                )
              }
            >
              Select All
            </button>
            <span className="text-[#D8D8D8]">|</span>
            <button
              className="text-xs text-[#1E90FF] cursor-pointer bg-transparent border-none p-0"
              onClick={() =>
                setDraftVisibility(
                  Object.fromEntries(draftOrder.map((id) => [id, false]))
                )
              }
            >
              Select None
            </button>
          </div>
        </div>

        <div className="overflow-auto h-full">
          {draftOrder.length === 0 ? (
            <div className="p-4 text-sm text-[#6B7280]">
              No columns available yet.
            </div>
          ) : (
            draftOrder.filter((id) => !OBSOLETE_COLUMN_IDS.has(id)).map((columnId) => (
              <div
                key={columnId}
                className={`px-3 py-2 border-b border-solid cursor-grab border-[#F1F1F1] flex items-center justify-between gap-3  ${
                  draggingColumnId === columnId ? "bg-[#EFF6FF]" : "bg-white"
                }`}
                draggable
                onDragStart={(event) => handleDragStart(event, columnId)}
                onDragOver={handleDragOver}
                onDrop={(event) => handleDrop(event, columnId)}
                onDragEnd={() => setDraggingColumnId(null)}
              >
              
                <div className="flex items-center gap-3 min-w-0">
                  <input
                  type="checkbox"
                  checked={draftVisibility[columnId] !== false}
                  onChange={() => handleToggleVisibility(columnId)}
                  onClick={(event) => event.stopPropagation()}
                  className="h-4 w-4 cursor-pointer"
                />
                  <span className="text-sm truncate">{COLUMN_DISPLAY_NAMES[columnId] ?? columnId}</span>
                </div>
                  <GripVertical size={16} className="text-[#9CA3AF] cursor-grab active:cursor-grabbing" />
                
              </div>
            ))
          )}
        </div>

        {selectNoneWarning && (
          <div className="px-4 py-2 bg-[#FFF3CD] border-t border-solid border-[#FFDCA0] text-xs text-[#856404]">
            Please select at least 2 columns before saving.
          </div>
        )}
        <div className="px-4 py-2 border-b border-solid border-[#EAEAEA] flex-col items-center justify-between">
          <p className="m-0 text-xs text-[#5E6772]">
            Drag to reorder. Use checkbox to show or hide columns.
          </p>
        </div>
        <div className="min-h-12 w-full bg-white border-x-0 border-t border-b-0 border-solid border-[#D8D8D8] flex items-center justify-evenly gap-4 px-4 py-2">
          <button
            className="h-8 max-w-[180px] w-full bg-white border-[#EAEAEA] border-solid outline-none rounded-xl text-sm font-medium cursor-pointer"
            onClick={handleReset}
            disabled={isSaving}
          >
            Reset
          </button>
          <button
            className={`h-8 max-w-[180px] w-full border-none rounded-xl text-sm font-medium cursor-pointer ${
              hasChanges ? "bg-[#A4D9FF]" : "bg-gray-200 text-gray-400"
            }`}
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default ColumnOrderDrawer;
